
import { Logger } from "#/lib/logging";
import { safeStringify } from "#/utils/general";
// import stringifyObject from "stringify-object";

function uniqBy<T>(array: T[], iteratee: ((item: T) => any) | keyof T): T[] {
  // Handle edge cases
  if (!Array.isArray(array) || array.length === 0) {
    return [];
  }

  // Map to track seen values
  const seen = new Map<any, boolean>();
  const result: T[] = [];

  // Normalize iteratee to a function
  const getValue = typeof iteratee === "function"
    ? iteratee
    : (item: T): T[keyof T] => item[iteratee];

  for (const item of array) {
    const key = getValue(item);
    if (!seen.has(key)) {
      seen.set(key, true);
      result.push(item);
    }
  }

  return result;
}

export const combineErrors = (errors: Error | Error[]): Error => {
  // Normalize to array
  const errorArray = Array.isArray(errors) ? errors : [ errors ];

  // De-duplicate errors based on stack trace
  const uniqueErrors = uniqBy(errorArray, (err: Error) => err.stack);

  // If there's only one error, return it directly
  if (uniqueErrors.length === 1) {
    return uniqueErrors[0];
  }

  // Create a new Error with combined messages
  const combinedError = new Error(
    uniqueErrors.filter(err => !!err.message.trim()).map(err => err.message).join("; ")
  );

  // Set the stack to the concatenation of all unique error stacks
  combinedError.stack = uniqueErrors
    .map(err => err.stack || `Error: ${err.message ?? "Unknown"}\n    (No stack available)`)
    .join("\n\n");

  combinedError.name = "CombinedAggregateError";

  return combinedError;
};

interface SerializedError {
  name: string,
  message?: string,
  stack?: string,
  cause?: unknown,
  [key: string]: any
}

interface ErrorLike {
  message?: string,
  stack?: string,
  cause?: unknown,
  name?: string
}

interface AggregateErrorLike {
  message?: string,
  errors: ErrorLike[],
  cause?: unknown,
  name?: string
}

/**
 * Converts a serialized error object into a proper Error instance
 * @param serializedError The serialized error object to convert
 * @returns A properly constructed Error instance
 */
export const errorFromDeserializedErrorObject = (serializedError: unknown): Error => {
  if (isAggregateError(serializedError)) {
    return consolidateAggregateError(serializedError);
  }

  if (
    serializedError instanceof Error
    && !!serializedError.constructor
  ) {
    return copyError(serializedError); // Use copyError instead of direct return
  }

  if (!serializedError || typeof serializedError !== "object" || !("name" in serializedError)) {
    return new Error(`Invalid deserialized error: ${safeStringify(serializedError)}`);
  }

  let clonedError: SerializedError;
  try {
    clonedError = structuredClone(serializedError as SerializedError);
  } catch (e) {
    Logger.warn({ error: e as Error, message: "Failed to clone error" });
    return new Error(`Clone failed: ${safeStringify(serializedError)}`);
  }

  const error = new Error(clonedError.message || clonedError.name || "Unknown Error");
  error.name = clonedError.name;
  clonedError.stack && (error.stack = clonedError.stack);
  clonedError.cause && (error.cause = clonedError.cause);

  // Use reduce instead of filter+forEach
  Object.entries(clonedError).reduce((acc, [ key, value ]) => {
    if (![ "name", "message", "stack", "cause" ].includes(key)) {
      (acc as any)[key] = value;
    }
    return acc;
  }, error);

  return error;
};

export const serializeError = (e?: any): string => {
  try {
    if (isAggregateError(e)) {
      return safeStringify(consolidateAggregateError(e));
    }
    if (e instanceof Error) {
      return safeStringify(e);
    }
    if (e == null) {
      return safeStringify(new Error(`Received ${e === null ? "null" : "undefined"} when serializing error`));
    }
    if (typeof e === "string") {
      return safeStringify(new Error(e));
    }
    if (typeof e === "number") {
      return safeStringify(new Error(`Thrown error contains just a number: ${e}`));
    }
    return safeStringify(errorFromDeserializedErrorObject(e));
  } catch (serializationError) {
    return safeStringify(new Error(`Failed to serialize error: ${String(e)}`));
  }
};
export const copyError = (error: Error): Error => {
  try {
    const newError = new (error.constructor as any)(error?.message ?? "Error with no message") as Error;
    return Object.defineProperties(newError, Object.getOwnPropertyDescriptors(error));
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const isAggregateError = (e: any): e is AggregateError => (
  e instanceof AggregateError
  || (!!(e as any)?.errors && Array.isArray((e as any).errors) && e.name === "AggregateError")
);

/**
 * Consolidates an AggregateError into a single Error instance
 * @param error The AggregateError to consolidate
 * @returns A consolidated Error instance
 */
export const consolidateAggregateError = (error: AggregateError): Error => {
  let errorCopy: AggregateErrorLike;
  try {
    errorCopy = {
      message: error.message,
      errors: structuredClone(error.errors),
      cause: error.cause,
      name: error.name
    };
  } catch (e) {
    Logger.warn({ message: "Failed to clone aggregate error", error: e as Error });
    return new Error(`Aggregation failed: ${safeStringify(error)}`);
  }

  const baseError = new Error(errorCopy.message || "Aggregate Error", {
    cause: errorCopy.cause
  });

  return combineErrors([
    baseError,
    ...errorCopy.errors.map(errorFromDeserializedErrorObject)
  ]);
};